import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ValueList, ValueLists } from '@ohpen/enso-ui';
import { ActionEntityType, Address, lenderFirmsGetTradingAddresses } from '@pmp/api';

import { formatAddressAsLines } from '../firms';
import { INTERMEDIARY_TRADING_ADDRESSES_LIST } from './tableConfig';

export type ResolveCellValueList = (column: { dataIndex?: unknown }, record: Record<string, unknown>) => ValueList | undefined;

const valueListKey = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value || undefined;
  if (value && typeof value === 'object' && 'key' in value) return (value as { key?: string | null }).key ?? undefined;
  return undefined;
};

export const useTradingAddressesInCR = (
  entityType: ActionEntityType | null,
  dataSource: readonly unknown[] | null,
): { resolveTradingAddressList?: ResolveCellValueList; valueLists: ValueLists; isLoading: boolean } => {
  const isBrokers = entityType === ActionEntityType.BROKERS;
  const [addressesByFirm, setAddressesByFirm] = useState<Record<string, ValueList>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isBrokers || !dataSource) return;
    const firmIds = Array.from(
      new Set(dataSource.map(row => valueListKey((row as { firmId?: unknown }).firmId)).filter((id): id is string => !!id)),
    );
    const missing = firmIds.filter(id => addressesByFirm[id] === undefined);
    if (!missing.length) return;

    let cancelled = false;
    setIsLoading(true);
    Promise.all(
      missing.map(firmId =>
        lenderFirmsGetTradingAddresses({ path: { firmId } })
          .then(({ data }) => {
            const list: ValueList = {};
            (data ?? [])
              .filter(address => address.isActive !== false && address.id)
              .forEach(address => {
                list[address.id!] = address.name || formatAddressAsLines(address as Address);
              });
            return [firmId, list] as const;
          })
          .catch(() => [firmId, {} as ValueList] as const),
      ),
    )
      .then(entries => {
        if (!cancelled) setAddressesByFirm(prev => ({ ...prev, ...Object.fromEntries(entries) }));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isBrokers, dataSource, addressesByFirm]);

  const resolveTradingAddressList = useCallback<ResolveCellValueList>(
    (column, record) => {
      if (column.dataIndex !== 'tradingAddressId') return undefined;
      const firmId = valueListKey(record.firmId);
      return firmId ? addressesByFirm[firmId] : undefined;
    },
    [addressesByFirm],
  );

  return useMemo<{ resolveTradingAddressList?: ResolveCellValueList; valueLists: ValueLists; isLoading: boolean }>(() => {
    if (!isBrokers) return { resolveTradingAddressList: undefined, valueLists: {}, isLoading: false };
    const merged: ValueList = Object.assign({}, ...Object.values(addressesByFirm));
    const valueLists: ValueLists = { [INTERMEDIARY_TRADING_ADDRESSES_LIST]: merged };
    return { resolveTradingAddressList, valueLists, isLoading };
  }, [isBrokers, addressesByFirm, resolveTradingAddressList, isLoading]);
};
